import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Download, ShieldCheck } from "lucide-react";

function ProgressRing({ percent = 0, size = 44 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  const color = percent >= 80 ? "#34D399" : percent >= 50 ? "#FBBF24" : "#FB7185";
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={off}
        style={{ filter: `drop-shadow(0 0 6px ${color}66)` }}
      />
    </svg>
  );
}

export default function Compliance() {
  const [data, setData] = useState({ rows: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/compliance");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const overall = useMemo(() => {
    if (!data.rows.length) return 0;
    const sum = data.rows.reduce((a, r) => a + (r.percent || 0), 0);
    return Math.round(sum / data.rows.length);
  }, [data]);

  const exportCSV = () => {
    const header = ["SOP", "Version", "Department", "Acks", "Total users", "Coverage %"];
    const rows = data.rows.map((r) => [r.title, r.version, r.department || "", r.acks, r.total_users, r.percent]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sopflow-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-white">Compliance & Audit</h1>
          <p className="text-[#9AA4B2] text-sm mt-1">Read & acknowledgement coverage across every approved SOP.</p>
        </div>
        <Button onClick={exportCSV} className="btn-ghost-glass rounded-full" data-testid="compliance-export">
          <Download size={14} className="mr-1.5" /> Export CSV
        </Button>
      </header>

      <div className="grid md:grid-cols-3 gap-4">
        <GlassCard className="p-6 md:col-span-1" data-testid="compliance-overall">
          <div className="text-xs uppercase tracking-widest text-[#9AA4B2]">Org-wide coverage</div>
          <div className="mt-2 flex items-end gap-3">
            <div className="font-display text-5xl text-white">{overall}%</div>
            <div className="mb-2 text-[#9AA4B2] text-sm">avg. across SOPs</div>
          </div>
          <div className="mt-4 h-2 rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400"
              style={{ width: `${overall}%`, boxShadow: "0 0 16px rgba(16,185,129,0.6)" }}
            />
          </div>
        </GlassCard>

        <GlassCard className="p-6 md:col-span-2" data-testid="compliance-callout">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center text-emerald-300">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="text-white font-display">Audit-ready trail</div>
              <div className="text-sm text-[#9AA4B2]">Every acknowledgement is signed and timestamped with device metadata for ISO 9001 / 27001 auditors.</div>
            </div>
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="compliance-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#5B6472] border-b border-white/10">
              <th className="py-3 px-5">SOP</th>
              <th className="py-3 px-5">Version</th>
              <th className="py-3 px-5">Department</th>
              <th className="py-3 px-5">Acks / Users</th>
              <th className="py-3 px-5">Coverage</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center text-[#5B6472]">Loading…</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-[#5B6472]">No approved SOPs yet.</td></tr>
            ) : (
              data.rows.map((r) => (
                <tr key={r.sop_id} className="border-b border-white/5 hover:bg-white/[0.03] transition">
                  <td className="py-3 px-5 text-white">{r.title}</td>
                  <td className="py-3 px-5 text-[#9AA4B2]">v{r.version}</td>
                  <td className="py-3 px-5 text-[#9AA4B2]">{r.department || "—"}</td>
                  <td className="py-3 px-5 text-[#9AA4B2]">{r.acks} / {r.total_users}</td>
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-3">
                      <ProgressRing percent={r.percent} />
                      <span className="text-white text-sm">{r.percent}%</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </GlassCard>
    </div>
  );
}
